import { useState, useRef, useEffect } from 'react';
import useStore from '../store/useStore';

// 转换为科学计数法格式
const formatToScientific = (num) => {
  if (!num) return '0';
  if (num < 10000) return num.toLocaleString();

  const formatNumber = (value, decimals) => {
    const formatted = value.toFixed(decimals);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
  };

  if (num >= 1e12) {
    const value = num / 1e12;
    if (value >= 100) return Math.floor(value) + 'T';
    if (value >= 10) return formatNumber(value, 1) + 'T';
    return formatNumber(value, 2) + 'T';
  }
  if (num >= 1e9) {
    const value = num / 1e9;
    if (value >= 100) return Math.floor(value) + 'B';
    if (value >= 10) return formatNumber(value, 1) + 'B';
    return formatNumber(value, 2) + 'B';
  }
  if (num >= 1e6) {
    const value = num / 1e6;
    if (value >= 100) return Math.floor(value) + 'M';
    if (value >= 10) return formatNumber(value, 1) + 'M';
    return formatNumber(value, 2) + 'M';
  }
  if (num >= 1e3) {
    const value = num / 1e3;
    if (value >= 100) return Math.floor(value) + 'K';
    if (value >= 10) return formatNumber(value, 1) + 'K';
    return formatNumber(value, 2) + 'K';
  }
  return num.toString();
};

const Header = () => {
  const { user, isAuthenticated, setLoginModalOpen, logout } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    setLoginModalOpen(true);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-[#dfe6ff] bg-white/75 backdrop-blur-xl dark:border-white/10 dark:bg-[rgba(6,2,18,0.85)]">
      <div className="mx-auto max-w-7xl px-3 py-1.5 sm:px-6 sm:py-2 lg:px-8">
        <div className="rounded-[1.15rem] border border-[#dfe6ff] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(243,247,255,0.88))] p-1.5 shadow-[0_10px_24px_rgba(100,120,194,0.08)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.09),rgba(255,255,255,0.05))] dark:shadow-[0_10px_22px_rgba(0,0,0,0.14)] sm:p-2">
          {isAuthenticated && user ? (
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
              {/* Player info */}
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-[0.95rem] border border-[#e7ebff] bg-white/72 px-2.5 py-2 dark:border-white/8 dark:bg-white/4 sm:gap-2.5 sm:px-3">
                <div className="relative">
                  {user.Avatar ? (
                    <img
                      src={user.Avatar}
                      alt={user.name}
                      className="h-8 w-8 rounded-full border-[2px] border-[#ffd54f] object-cover shadow-[0_6px_14px_rgba(98,106,170,0.12)] sm:h-9 sm:w-9"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border-[2px] border-[#ffd54f] bg-[linear-gradient(145deg,#ffffff,#eef2ff)] text-sm font-black text-[#2d254f] shadow-[0_6px_14px_rgba(98,106,170,0.12)] sm:h-9 sm:w-9">
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-black uppercase text-[#2a2150] dark:text-white sm:text-[1.3rem]">
                    {user.name}
                  </p>
                  <p className="mt-0.5 truncate text-[10px] font-medium text-[#6f7896] dark:text-white/50 sm:text-xs">
                    ID: {user.id}
                  </p>
                </div>
                {/* Logout button */}
                <div className="relative flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e7ebff] bg-[#f2f5fb] text-[#6f7896] transition hover:bg-[#e8ecf8] hover:text-[#2a2150] dark:border-white/10 dark:bg-white/5 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v.01M12 12v.01M12 18v.01" />
                    </svg>
                  </button>
                  {menuOpen && (
                    <div
                      ref={menuRef}
                      className="absolute right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-[#dfe6ff] bg-white shadow-[0_12px_32px_rgba(0,0,0,0.1)] backdrop-blur-xl dark:border-white/10 dark:bg-[rgba(20,10,40,0.95)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.5)]"
                    >
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-5 py-3 text-sm font-semibold text-[#6f7896] transition hover:bg-[#f2f5fb] hover:text-[#2a2150] dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden h-10 w-px bg-[#dde4ff] dark:bg-white/10 xl:block" />

              {/* Stats cards */}
              <div className="grid min-w-0 flex-[1.25] grid-cols-2 gap-2 lg:grid-cols-4 sm:gap-2.5">
                {/* VIP */}
                <div className="min-w-0 rounded-[0.95rem] border border-[#f6d36a] bg-[linear-gradient(180deg,#fff7df_0%,#fff0c8_100%)] px-2.5 py-2 dark:border-[#7b6522] dark:bg-[linear-gradient(180deg,rgba(255,213,84,0.12),rgba(255,213,84,0.06))] sm:px-3">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#b78b1e] dark:text-[#ffd456]/70 sm:tracking-[0.28em]">
                    VIP
                  </p>
                  <p className="mt-1 truncate font-display text-[1.05rem] font-black uppercase text-[#a56e00] dark:text-[#ffd321] sm:text-[1.3rem]">
                    VIP {user.VipLevel || 0}
                  </p>
                </div>

                {/* Gems */}
                <div className="min-w-0 rounded-[0.95rem] border border-[#88dcff] bg-[linear-gradient(180deg,#eef9ff_0%,#dff4ff_100%)] px-2.5 py-2 dark:border-[#296589] dark:bg-[linear-gradient(180deg,rgba(80,196,255,0.13),rgba(80,196,255,0.05))] sm:px-3">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#2c91c3] dark:text-[#53dcff]/70 sm:tracking-[0.28em]">
                    Gems
                  </p>
                  <p className="mt-1 truncate font-display text-[1.05rem] font-black uppercase text-[#198bc8] dark:text-[#16d4ff] sm:text-[1.3rem]">
                    {formatToScientific(user.Gem || 0)}
                  </p>
                </div>

                {/* Coins */}
                <div className="min-w-0 rounded-[0.95rem] border border-[#f6d36a] bg-[linear-gradient(180deg,#fff7df_0%,#fff0c8_100%)] px-2.5 py-2 dark:border-[#7b6522] dark:bg-[linear-gradient(180deg,rgba(255,213,84,0.12),rgba(255,213,84,0.06))] sm:px-3">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#b78b1e] dark:text-[#ffd456]/70 sm:tracking-[0.28em]">
                    Coins
                  </p>
                  <p className="mt-1 truncate font-display text-[1.05rem] font-black uppercase text-[#a56e00] dark:text-[#ffd321] sm:text-[1.3rem]">
                    {formatToScientific(user.Coin || 0)}
                  </p>
                </div>

                {/* Level */}
                <div className="min-w-0 rounded-[0.95rem] border border-[#ff9aa5] bg-[linear-gradient(180deg,#fff0f3_0%,#ffe2e7_100%)] px-2.5 py-2 dark:border-[#81414d] dark:bg-[linear-gradient(180deg,rgba(255,113,127,0.12),rgba(255,113,127,0.05))] sm:px-3">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#d06477] dark:text-[#ff8b96]/70 sm:tracking-[0.28em]">
                    Level
                  </p>
                  <p className="mt-1 truncate font-display text-[1.05rem] font-black uppercase text-[#db6678] dark:text-[#ff717f] sm:text-[1.3rem]">
                    LV.{user.Level || 0}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2.5">
                <img src="assets/icon.png" alt="Store Icon" className="h-8 w-8 object-contain" />
                <p className="font-display text-lg font-black uppercase tracking-wide text-[#2a2150] dark:text-white">
                  Fantasy Slots Store
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setLoginModalOpen(true)}
                  className="rounded-full border border-gold/40 bg-gold/10 px-5 py-2 font-display text-sm font-black uppercase tracking-[0.12em] text-gold transition hover:bg-gold/20"
                >
                  Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
